export type FaceSearchErrorCode =
    | "FACE_NOT_DETECTED"
    | "MULTIPLE_FACES"
    | "IMAGE_UNREADABLE"
    | "NO_FACE_MATCH"
    | "FACE_SEARCH_FAILED"
    | "UNKNOWN";

export type FaceSearchError = {
    code: FaceSearchErrorCode;
    message: string;
    detail?: string;
};

const ERROR_COPY: Record<
    FaceSearchErrorCode,
    { title: string; fallbackMessage: string }
> = {
    FACE_NOT_DETECTED: {
        title: "No face detected",
        fallbackMessage: "We could not find a face in this image. Please retake the photo.",
    },
    MULTIPLE_FACES: {
        title: "Multiple faces detected",
        fallbackMessage: "Please retake the photo with only one clear face.",
    },
    IMAGE_UNREADABLE: {
        title: "Server error",
        fallbackMessage: "The uploaded image could not be read. Please try again later.",
    },
    NO_FACE_MATCH: {
        title: "No match found",
        fallbackMessage: "No voter match was found for this face.",
    },
    FACE_SEARCH_FAILED: {
        title: "Server error",
        fallbackMessage: "Face recognition is not possible right now. Please try again later.",
    },
    UNKNOWN: {
        title: "Unable to search",
        fallbackMessage: "Unable to fetch service requests by face.",
    },
};

export const FACE_RETAKE_CODES: FaceSearchErrorCode[] = [
    "MULTIPLE_FACES",
    "FACE_NOT_DETECTED",
];

export const FACE_SERVER_ERROR_CODES: FaceSearchErrorCode[] = [
    "IMAGE_UNREADABLE",
    "FACE_SEARCH_FAILED",
];

export const isFaceRetakeError = (error: FaceSearchError | null) =>
    Boolean(error && FACE_RETAKE_CODES.includes(error.code));

export const isFaceServerError = (error: FaceSearchError | null) =>
    Boolean(error && FACE_SERVER_ERROR_CODES.includes(error.code));

export const getFaceSearchErrorCopy = (error: FaceSearchError) => {
    const copy = ERROR_COPY[error.code] || ERROR_COPY.UNKNOWN;

    return {
        title: copy.title,
        message: error.message || copy.fallbackMessage,
        detail: error.detail || "",
        actionLabel: isFaceRetakeError(error) ? "Retake" : "Try again",
    };
};

const knownCodes = Object.keys(ERROR_COPY) as FaceSearchErrorCode[];

const resolveErrorCode = (data: any, status?: number): FaceSearchErrorCode => {
    const code = data?.code as FaceSearchErrorCode;
    if (knownCodes.includes(code)) return code;
    if (status === 422) return "FACE_NOT_DETECTED";
    if (status === 400) return "FACE_SEARCH_FAILED";
    return "UNKNOWN";
};

export const parseFaceSearchError = (response: any): FaceSearchError => {
    const data = response?.data || response || {};
    const status = response?.status as number | undefined;

    return {
        code: resolveErrorCode(data, status),
        message: data?.message || "",
        detail: data?.detail || "",
    };
};
